import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Copy, ShareNetwork } from "@phosphor-icons/react";
import { LIBRARY_SECTIONS } from "../data/libraryVideos";
import { copyToClipboard } from "../lib/clipboard";

const GOLD = "#c9a24a";
const LANGS = ["en", "es", "ru", "fr"];

// v414.80 — shared Video Library body: grouped sections, language filter
// chips, copy-link + share per card. Used by the distributor portal page AND
// the public storefront page — carries ZERO pricing/cost/code information.
// v414.90 — clips matching the visitor's UI language list first (so Spanish
// stores lead with ES videos); ES added to the filter chips.
export default function VideoLibrary() {
  const { t, i18n } = useTranslation();
  const [lang, setLang] = useState("all");
  const uiLang = (i18n.resolvedLanguage || "en").split("-")[0];

  const sections = useMemo(() =>
    LIBRARY_SECTIONS
      .map((s) => ({
        ...s,
        videos: s.videos
          .filter((v) => lang === "all" || v.lang === lang)
          .slice()
          .sort((a, b) => (a.lang === uiLang ? 0 : 1) - (b.lang === uiLang ? 0 : 1)),
      }))
      .filter((s) => s.videos.length > 0),
  [lang, uiLang]);

  return (
    <div className="space-y-8" data-testid="video-library">
      {/* language filter */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="library-lang-filter">
        {["all", ...LANGS].map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => setLang(l)}
            data-testid={`library-lang-${l}`}
            className={`h-8 px-3.5 rounded-full border text-[11px] font-semibold uppercase tracking-wide transition-colors ${
              lang === l
                ? "bg-zinc-950 text-[#c9a24a] border-[#c9a24a]"
                : "bg-white text-zinc-500 border-zinc-200 hover:bg-zinc-50 hover:text-zinc-800"
            }`}
          >
            {l === "all" ? t("library.filter_all") : l}
          </button>
        ))}
      </div>

      {sections.map((s) => (
        <section key={s.key} data-testid={`library-section-${s.key}`}>
          <h2 className="text-base md:text-lg font-semibold text-zinc-950 flex items-center gap-2 mb-3">
            <span className="w-1.5 h-5 rounded-full" style={{ background: GOLD }} />
            {t(`library.section_${s.key}`)}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
            {s.videos.map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function VideoCard({ video }) {
  const { t } = useTranslation();
  const title = t(video.title);
  const shareText = t("library.share_text", { name: title });

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: shareText, url: video.url });
        return;
      } catch {
        /* sheet closed — fall through to copy */
      }
    }
    const ok = await copyToClipboard(`${shareText}\n${video.url}`);
    toast[ok ? "success" : "error"](ok ? t("library.copied") : t("library.copy_failed"));
  };

  const copyLink = async () => {
    const ok = await copyToClipboard(video.url);
    toast[ok ? "success" : "error"](ok ? t("library.copied") : t("library.copy_failed"));
  };

  return (
    <div
      className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden flex flex-col"
      data-testid={`library-card-${video.id}`}
    >
      <div className="px-3 pt-3 pb-2">
        <div className="flex items-start justify-between gap-1.5">
          <h3 className="text-sm font-semibold text-zinc-950 leading-snug" title={title}>
            {title}
          </h3>
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              type="button"
              onClick={copyLink}
              title={t("library.copy_link")}
              className="w-7 h-7 rounded-md flex items-center justify-center text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100 transition-colors"
              data-testid={`library-copy-${video.id}`}
            >
              <Copy size={14} weight="bold" />
            </button>
            <button
              type="button"
              onClick={share}
              title={t("library.share")}
              className="w-7 h-7 rounded-md flex items-center justify-center text-[#8a6a1c] hover:bg-[#fbf6e7] transition-colors"
              data-testid={`library-share-${video.id}`}
            >
              <ShareNetwork size={14} weight="bold" />
            </button>
          </div>
        </div>
        <div className="mt-1.5 flex items-center gap-1.5">
          <span
            className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-bold uppercase tracking-wide bg-zinc-950 text-[#c9a24a]"
            data-testid={`library-lang-badge-${video.id}`}
          >
            {video.lang}
          </span>
          {video.dur && (
            <span className="text-[11px] text-zinc-400">{video.dur}s</span>
          )}
        </div>
      </div>
      <div className="px-3 pb-3 mt-auto">
        <video
          src={video.url}
          poster={`/posters/${video.id}.jpg`}
          controls
          playsInline
          preload="none"
          className="w-full aspect-[9/16] rounded-lg bg-zinc-950 object-contain"
          data-testid={`library-player-${video.id}`}
        />
      </div>
    </div>
  );
}
